import { ContentAction } from "@/domain/entities/vistassistant/AIResponse";
import { cn } from "@/lib/utils";
import { CheckCheck, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";
import {
    primaryButtonClassName,
    secondaryButtonClassName,
} from "./assistantClasses";
import ConversationBubble from "./ConversationBubble";
import getVistassistantActionLabels from "./vistassistantActionLabels";

const destructiveButtonClassName =
    "h-9 rounded-full border-0 bg-error-darkest px-4 text-sm font-semibold text-white hover:bg-error-darkest/90 hover:text-white";

interface ActionConfirmationProps {
    contentAction?: ContentAction;
    /** Mails of the answer as received: an action on a list empty from the start gets no bubble. */
    receivedCount: number;
    /** Mails of the answer as displayed ("load more" included). */
    displayedCount: number;
    actionDone?: boolean;
    isLastMessage: boolean;
    onAction?: () => void | Promise<void>;
}

/**
 * The confirmation of an action the answer proposes on its mails, in the thread (product choice):
 * « Supprimer ces 12 mails ? », « Annuler » and the action. Only the last message can still run it.
 * Debt: labels from getVistassistantActionLabels + enabled/disabled/success rules inferred on the
 * client.
 */
const ActionConfirmation = ({
    contentAction,
    receivedCount,
    displayedCount,
    actionDone,
    isLastMessage,
    onAction,
}: ActionConfirmationProps) => {
    const { t } = useTranslation();
    // Count frozen on click: a deletion empties the list, the bubble keeps the number it applied
    // to; no second click while the action runs.
    const [actedCount, setActedCount] = useState<number | null>(null);
    const [isActing, setIsActing] = useState(false);
    // « Annuler » on the proposal: nothing runs, the card says so.
    const [isDismissed, setIsDismissed] = useState(false);
    // F-1.7: the confirmation gives the number of mails the action applies to.
    const labelCount =
        (actionDone || isActing) && actedCount !== null
            ? actedCount
            : displayedCount;
    const actionLabels = useMemo(
        () =>
            contentAction
                ? getVistassistantActionLabels(contentAction, t, labelCount)
                : null,
        [contentAction, t, labelCount],
    );

    if (!actionLabels || receivedCount === 0) return null;

    // The list emptied after display (mails deleted or moved one by one): nothing left to act
    // on, say so instead of a button that would do nothing.
    const isNothingLeft = !actionDone && !isActing && displayedCount === 0;
    if (isNothingLeft) {
        return (
            <ConversationBubble>
                <p>{t("vistassistant.actions.noMailLeft")}</p>
            </ConversationBubble>
        );
    }

    const isDestructive = contentAction === ContentAction.DELETE;
    const isOpen = !actionDone && !isDismissed;
    const confirmClassName = isDestructive
        ? destructiveButtonClassName
        : primaryButtonClassName;

    const handleAction = async () => {
        if (!onAction || isActing) return;
        setActedCount(displayedCount);
        setIsActing(true);
        try {
            await onAction();
        } finally {
            setIsActing(false);
        }
    };

    return (
        <ConversationBubble className="flex flex-wrap items-center gap-x-3 gap-y-2.5 whitespace-normal">
            <div
                className={cn(
                    "flex size-8 flex-none items-center justify-center rounded-lg [&_svg]:size-[18px]",
                    isDestructive
                        ? "bg-error-lightest text-error-darkest"
                        : "bg-assistant-lavender text-primary-dark",
                )}
            >
                {isDestructive ? (
                    <Trash2 aria-hidden />
                ) : (
                    <CheckCheck aria-hidden />
                )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
                <p className="text-base font-semibold">
                    {actionLabels.message}
                </p>
                {actionDone && (
                    <span className="flex animate-opacity items-center gap-1.5 text-[13px] text-success-darkest">
                        <CheckCheck aria-hidden className="h-3.5 w-3.5" />
                        {t("vistassistant.actions.success")}
                    </span>
                )}
                {!actionDone && isDismissed && (
                    <span className="text-[13px] text-assistant-muted">
                        {t("vistassistant.actions.cancelled")}
                    </span>
                )}
                {isOpen && (
                    <span className="text-xs text-assistant-muted">
                        {t("vistassistant.actions.confirmHint")}
                    </span>
                )}
            </div>
            {isOpen && isLastMessage && (
                <div className="ml-auto flex flex-none gap-2">
                    <Button
                        className={secondaryButtonClassName}
                        disabled={isActing}
                        onClick={() => setIsDismissed(true)}
                        type="button"
                        variant="outline"
                    >
                        {t("vistassistant.actions.cancel")}
                    </Button>
                    <Button
                        className={confirmClassName}
                        disabled={isActing}
                        onClick={handleAction}
                        type="button"
                        variant="outline"
                    >
                        {actionLabels.button}
                    </Button>
                </div>
            )}
            {/* Superseded: a later question moved on, the action can no longer run. */}
            {isOpen && !isLastMessage && (
                <div className="ml-auto flex-none">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span>
                                    <Button
                                        className={confirmClassName}
                                        type="button"
                                        variant="outline"
                                        disabled
                                    >
                                        {actionLabels.button}
                                    </Button>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                {t("vistassistant.actions.outdated")}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            )}
        </ConversationBubble>
    );
};

export default ActionConfirmation;
